import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../services/api';
import { formatDuration } from '../../utils/duration';

const UserActivityPage = () => {
  const navigate = useNavigate();
  const { activeUsersCount } = useSocket();
  const [sessionStats, setSessionStats] = useState({});
  const [sessionHistory, setSessionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
    minDuration: '',
    maxDuration: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchData();
  }, [activeUsersCount, filters.page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch session stats
      const statsResponse = await api.get('/sessions/stats');
      setSessionStats(statsResponse.data.data || {});

      // Fetch session history
      const historyResponse = await api.get('/sessions/history', {
        params: filters
      });
      setSessionHistory(historyResponse.data.data.sessions || []);
      setPagination(historyResponse.data.data.pagination || {});
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const exportToCSV = () => {
    const csvContent = [
      ['User', 'Login Time', 'Logout Time', 'Duration', 'Status'],
      ...sessionHistory.map(session => [
        session.userId?.name || 'Unknown',
        formatDate(session.loginTime),
        session.logoutTime ? formatDate(session.logoutTime) : 'Active',
        formatDuration(session.sessionDuration),
        session.isActive ? 'Active' : 'Ended'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-sessions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-admin-slate-800 to-admin-slate-700 dark:from-admin-slate-900 dark:to-admin-slate-800 p-8">
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-white mb-2">User Activity Monitoring</h1>
          <p className="text-admin-slate-200">
            Real-time user activity and session tracking
          </p>
        </div>
        <div className="absolute right-0 top-0 w-64 h-64 bg-gradient-to-br from-admin-ucla-500/20 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <button
          type="button"
          onClick={() => navigate('/admin/online-users')}
          className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm text-left hover:shadow-md hover:ring-1 hover:ring-admin-ucla-500 transition-all duration-200 cursor-pointer"
          title="View Currently Online Users"
        >
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Active Now</p>
              <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                {sessionStats.activeUsersNow || 0}
              </p>
            </div>
          </div>
        </button>

        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Avg Session</p>
              <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                {sessionStats.averageSessionLengthToday || 0}m
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Peak Today</p>
              <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                {sessionStats.peakConcurrentUsersToday || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">Sessions Today</p>
              <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                {sessionStats.totalSessionsToday || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Session History */}
      <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100">
            Session History
          </h2>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
              Min Duration (min)
            </label>
            <input
              type="number"
              value={filters.minDuration}
              onChange={(e) => handleFilterChange('minDuration', e.target.value)}
              className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
              Max Duration (min)
            </label>
            <input
              type="number"
              value={filters.maxDuration}
              onChange={(e) => handleFilterChange('maxDuration', e.target.value)}
              className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchData}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Session History Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-admin-slate-200 dark:border-admin-slate-700">
                <th className="text-left py-3 px-4 font-medium text-admin-slate-700 dark:text-admin-slate-300">User</th>
                <th className="text-left py-3 px-4 font-medium text-admin-slate-700 dark:text-admin-slate-300">Login Time</th>
                <th className="text-left py-3 px-4 font-medium text-admin-slate-700 dark:text-admin-slate-300">Logout Time</th>
                <th className="text-left py-3 px-4 font-medium text-admin-slate-700 dark:text-admin-slate-300">Duration</th>
                <th className="text-left py-3 px-4 font-medium text-admin-slate-700 dark:text-admin-slate-300">Status</th>
              </tr>
            </thead>
            <tbody>
              {sessionHistory.map((session) => (
                <tr key={session._id} className="border-b border-admin-slate-100 dark:border-admin-slate-700">
                  <td className="py-3 px-4">
                    <div
                      className={`flex items-center gap-3 ${session.userId?._id ? 'cursor-pointer group' : ''}`}
                      onClick={() => session.userId?._id && navigate(`/admin/customers?openCustomer=${session.userId._id}`)}
                    >
                      <div className="w-8 h-8 rounded-full bg-admin-ucla-100 dark:bg-admin-ucla-900/30 flex items-center justify-center">
                        <span className="text-admin-ucla-600 dark:text-admin-ucla-400 font-medium text-sm">
                          {session.userId?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-admin-slate-900 dark:text-admin-slate-100 group-hover:text-admin-ucla-600 dark:group-hover:text-admin-ucla-400 group-hover:underline">
                          {session.userId?.name || 'Unknown User'}
                        </p>
                        <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                          {session.userId?.email || 'No email'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-admin-slate-700 dark:text-admin-slate-300">
                    {formatDate(session.loginTime)}
                  </td>
                  <td className="py-3 px-4 text-admin-slate-700 dark:text-admin-slate-300">
                    {session.logoutTime ? formatDate(session.logoutTime) : '-'}
                  </td>
                  <td className="py-3 px-4 text-admin-slate-700 dark:text-admin-slate-300">
                    {formatDuration(session.sessionDuration)}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      session.isActive 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' 
                        : 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400'
                    }`}>
                      {session.isActive ? 'Active' : 'Ended'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-admin-slate-700 dark:text-admin-slate-300">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange('page', pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handleFilterChange('page', pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActivityPage;
